export const STALE_CHUNK_RELOAD_STORAGE_KEY = "ap_stale_chunk_reload";

const CHUNK_LOAD_MESSAGE_PATTERNS = [
  /failed to load chunk/i,
  /loading chunk [\w./-]+ failed/i,
  /loading css chunk/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
];

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export type StaleChunkMemoryGuard = {
  consumed: boolean;
};

export type StaleChunkReloadDeps = {
  storage?: StorageLike | null;
  reload?: () => void;
  memoryGuard?: StaleChunkMemoryGuard;
};

const defaultMemoryGuard: StaleChunkMemoryGuard = { consumed: false };

export function resetStaleChunkReloadMemoryGuard() {
  defaultMemoryGuard.consumed = false;
}

function matchesChunkLoadMessage(message: string): boolean {
  return CHUNK_LOAD_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

function readErrorName(error: object): string {
  return "name" in error && typeof error.name === "string" ? error.name : "";
}

function readErrorMessage(error: object): string {
  return "message" in error && typeof error.message === "string"
    ? error.message
    : "";
}

export function isChunkLoadError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current && !seen.has(current)) {
    seen.add(current);

    if (typeof current === "string") {
      return matchesChunkLoadMessage(current);
    }

    if (typeof current === "object") {
      if (readErrorName(current) === "ChunkLoadError") {
        return true;
      }
      if (matchesChunkLoadMessage(readErrorMessage(current))) {
        return true;
      }
      current = "cause" in current ? current.cause : undefined;
      continue;
    }

    break;
  }

  return false;
}

function defaultReload() {
  if (typeof window === "undefined") {
    return;
  }
  window.location.reload();
}

function resolveSessionStorage(): StorageLike | null {
  try {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage;
  } catch {
    return null;
  }
}

function hasStorageGuard(storage: StorageLike): boolean {
  return storage.getItem(STALE_CHUNK_RELOAD_STORAGE_KEY) === "1";
}

function markStorageGuard(storage: StorageLike) {
  storage.setItem(STALE_CHUNK_RELOAD_STORAGE_KEY, "1");
}

export function reloadPageOnceForStaleChunk(
  deps: StaleChunkReloadDeps = {},
): boolean {
  const reload = deps.reload ?? defaultReload;
  const memoryGuard = deps.memoryGuard ?? defaultMemoryGuard;
  const storage =
    deps.storage !== undefined ? deps.storage : resolveSessionStorage();

  if (storage) {
    try {
      if (hasStorageGuard(storage)) {
        return false;
      }
      markStorageGuard(storage);
    } catch {
      if (memoryGuard.consumed) {
        return false;
      }
      memoryGuard.consumed = true;
      reload();
      return true;
    }
  } else if (memoryGuard.consumed) {
    return false;
  } else {
    memoryGuard.consumed = true;
  }

  reload();
  return true;
}

export function recoverFromChunkLoadError(
  error: unknown,
  deps: StaleChunkReloadDeps = {},
): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }
  return reloadPageOnceForStaleChunk(deps);
}

export function retryAfterBoundaryError(
  error: unknown,
  reset: () => void,
  reload: () => void = defaultReload,
) {
  if (isChunkLoadError(error)) {
    reload();
    return;
  }
  reset();
}
